import React from 'react';
import { Link } from 'react-router-dom';

const NoteCard = ({ note, linkTo, isSearchResult }) => {
    
    const formattedDate = note.date ? new Date(note.date).toLocaleDateString() : 'N/A';
    const displayTopic = note.displayTopic || note.topic;
    
    const cardGradients = {
        'c': 'from-blue-500 to-blue-600',
        'c++': 'from-purple-500 to-purple-600',
        'python': 'from-green-500 to-green-600',
        'java': 'from-red-500 to-red-600',
        'html': 'from-orange-500 to-orange-600',
        'css': 'from-blue-400 to-blue-500',
        'javascript': 'from-yellow-500 to-yellow-600',
        'react': 'from-cyan-500 to-cyan-600',
        'nodejs': 'from-green-400 to-green-500',
        'docker': 'from-blue-300 to-blue-400',
        'git': 'from-orange-400 to-orange-500',
        'github': 'from-gray-600 to-gray-700',
        'dsa': 'from-purple-400 to-purple-500'
    };

    const gradient = cardGradients[displayTopic] || 'from-blue-400 to-blue-500';

    return (
        <div className={`
            group relative bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden 
            transform hover:scale-[1.02] sm:hover:scale-[1.03] transition-all duration-300 ease-out
            border border-gray-200 hover:border-blue-300 hover:shadow-lg sm:hover:shadow-2xl
            flex flex-col justify-between h-full min-h-[180px] sm:min-h-[200px]
            ${isSearchResult ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
        `}>
            {/* Background Glow Effect */}
            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
            
            {/* Header - Mobile Optimized */}
            <div className={`relative bg-gradient-to-r ${gradient} p-3 sm:p-4 text-white overflow-hidden`}>
                <div className="absolute inset-0 bg-black opacity-5"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-2">
                        <span className="text-xs font-bold uppercase bg-white bg-opacity-20 px-2 py-0.5 sm:py-1 rounded-full backdrop-blur-sm tracking-wide">
                            {displayTopic.toUpperCase()}
                        </span>
                        {/* Improved Title Typography */}
                        <h3 className="font-bold mt-1.5 sm:mt-2 leading-tight line-clamp-2 
                            text-sm sm:text-base 
                            font-sans text-white 
                            tracking-tight 
                            drop-shadow-sm">
                            {note.title}
                        </h3>
                    </div>
                    
                    {/* Icon Badge */}
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-white bg-opacity-20 rounded sm:rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <span className="text-xs sm:text-sm font-bold font-mono">
                            {displayTopic.charAt(0).toUpperCase()}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Content Area - Better Mobile Spacing */}
            <div className="p-3 sm:p-4 flex-grow flex flex-col relative z-10">
                {/* Improved Description Typography */}
                <p className="text-gray-700 mb-2 sm:mb-3 line-clamp-2 sm:line-clamp-3 flex-grow 
                    text-xs sm:text-sm 
                    leading-relaxed sm:leading-normal
                    font-normal 
                    font-sans 
                    tracking-normal">
                    {note.description}
                </p>
                
                {/* Tags - Mobile Optimized */}
                <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                    {note.tags.slice(0, 2).map(tag => (
                        <span 
                            key={tag} 
                            className="font-medium bg-blue-50 text-blue-700 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded sm:rounded-lg border border-blue-200
                                text-xs 
                                font-sans 
                                tracking-tight
                                hover:bg-blue-100 hover:text-blue-800 transition-colors duration-200"
                        >
                            {tag}
                        </span>
                    ))}
                    {note.tags.length > 2 && (
                        <span className="text-gray-500 bg-gray-100 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded sm:rounded-lg border border-gray-200
                            text-xs 
                            font-sans 
                            tracking-tight">
                            +{note.tags.length - 2}
                        </span>
                    )}
                </div>
            </div>
            
            {/* Footer - Mobile Compact */}
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-2 sm:pt-3 bg-gray-50 border-t border-gray-100 relative z-10">
                <div className="flex justify-between items-center gap-2">
                    {/* Date - Improved Typography */}
                    <div className="flex items-center space-x-1 sm:space-x-1.5 flex-shrink-0">
                        <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-gray-600 whitespace-nowrap
                            text-xs 
                            font-medium 
                            font-sans 
                            tracking-wide">
                            {formattedDate}
                        </span>
                    </div>
                    
                    {/* Button - Mobile Optimized with Better Typography */}
                    <Link
                        to={linkTo}
                        className={`
                            inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-600 
                            hover:from-blue-600 hover:to-blue-700 text-white 
                            py-1.5 px-2 sm:py-2 sm:px-3 rounded sm:rounded-lg transition-all duration-300 
                            shadow hover:shadow-md transform hover:scale-105
                            group-hover:shadow-lg whitespace-nowrap flex-shrink-0
                            text-xs sm:text-sm
                            font-semibold
                            font-sans
                            tracking-wide
                            hover:shadow-blue-200
                        `}
                    >
                        {linkTo && linkTo.split('/').length <= 2 ? 'Explore' : 'Read'}
                        <span className="ml-0.5 sm:ml-1 transform group-hover:translate-x-0.5 transition-transform duration-200">→</span>
                    </Link>
                </div>
            </div>

            {/* Hover Border Effect */}
            <div className={`absolute inset-0 rounded-xl sm:rounded-2xl border-2 border-transparent group-hover:border-white group-hover:border-opacity-30 transition-all duration-300 pointer-events-none`}></div>
        </div>
    );
};

export default NoteCard;
